import { Alert, Button, Stack, Typography } from '@mui/material';
import LinearProgress from '@mui/material/LinearProgress';
import * as Immutable from 'immutable';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import DialogContent from '../../../Common/Dialog/DialogContent';
import { useAppDispatch } from '../../../Common/_hooks/useAppDispatch';
import { useAppSelector } from '../../../Common/_hooks/useAppSelector';
import { FetchStoreData } from '../../../UserData/_actions/Store/StoreActions';
import type ProductPublishJobStore from '../../../UserData/_stores/Store/ProductPublish/ProductPublishJobStore';
import type { ProductPublishJobStatus } from '../../../UserData/_stores/Store/ProductPublish/ProductPublishJobStore';
import ProductPublishJobList from './ProductPublishJobList';

export interface Props {
	store: string
	batchId: string
	isSingle?: boolean
	isDonePublishing?: boolean
	onDialogClose: () => void
}

type BatchProductPublishSummary = Record<ProductPublishJobStatus, number> & {
	total: number,
	active: number,
	finished: boolean,
}

const emptySummary: BatchProductPublishSummary = {
	total: 0,
	pending: 0,
	processing: 0,
	published: 0,
	failed: 0,
	cancelled: 0,
	active: 0,
	finished: false,
}

const emptyJobs = Immutable.List<ProductPublishJobStore>()

export default function Publishing(props: Props) {
	const dispatch = useAppDispatch()
	const [t] = useTranslation('account-v2')
	const lastNonEmptySummaryRef = useRef<{
		batchId: string,
		summary: BatchProductPublishSummary,
	} | null>(null)
	const publishingJobs = useAppSelector(state => state
		.get('userData')
		.get('stores')
		.find(store => store.get('slug') === props.store)
		?.get('product_publish_summary')
		.get('jobs')
	)

	const jobs = useMemo(() => (
		(publishingJobs ?? emptyJobs).filter(job => job.get('batch_id') === props.batchId)
	), [props.batchId, publishingJobs])

	const summary = useMemo(() => {
		const batchSummary = {
			...emptySummary,
		}

		jobs.forEach(job => {
			batchSummary.total++

			switch (job.get('status')) {
				case 'processing':
					batchSummary.processing++
					break
				case 'published':
					batchSummary.published++
					break
				case 'failed':
					batchSummary.failed++
					break
				case 'cancelled':
					batchSummary.cancelled++
					break
				default:
					batchSummary.pending++
					break
			}
		})

		batchSummary.active = batchSummary.pending + batchSummary.processing
		batchSummary.finished = batchSummary.total > 0 && batchSummary.active === 0

		return batchSummary
	}, [jobs])

	if (summary.total > 0) {
		lastNonEmptySummaryRef.current = {
			batchId: props.batchId,
			summary,
		}
	}

	const visibleSummary = summary.total > 0
		? summary
		: (
			lastNonEmptySummaryRef.current?.batchId === props.batchId
				&& lastNonEmptySummaryRef.current.summary.finished
				? lastNonEmptySummaryRef.current.summary
				: summary
		)

	const refreshJobs = useCallback(() => {
		if (!props.batchId) return;

		dispatch(FetchStoreData(props.store))
	}, [dispatch, props.batchId, props.store])

	useEffect(() => {
		refreshJobs()
	}, [refreshJobs])

	useEffect(() => {
		if (!props.batchId || visibleSummary.finished) {
			return
		}

		const timer = setInterval(refreshJobs, 5000)

		return () => clearInterval(timer)
	}, [props.batchId, refreshJobs, visibleSummary.finished])

	const done = visibleSummary.published + visibleSummary.failed + visibleSummary.cancelled
	const progress = visibleSummary.total > 0 ? (done / visibleSummary.total) * 100 : 0

	const progressLabel = useMemo(() => {
		if (visibleSummary.finished && visibleSummary.failed === 0) {
			return t('All done!')
		}

		if (visibleSummary.finished) {
			return t('{{published}} of {{total}} products published', {
				published: visibleSummary.published,
				total: visibleSummary.total,
			})
		}

		return t('Publishing {{value}} of {{total}} products', {
			value: Math.min(done + 1, Math.max(visibleSummary.total, 1)),
			total: Math.max(visibleSummary.total, 1),
		})
	}, [done, visibleSummary.finished, visibleSummary.failed, visibleSummary.published, visibleSummary.total, t])

	return <>
		<DialogContent sx={{alignItems: 'center'}}>
			<Stack spacing={2} alignItems="center">
				<Typography variant="h3">{t('Publishing products')}</Typography>
				<Typography variant="body2">{t('You can close this window while products are being published in the background.')}</Typography>

				<Stack spacing={1} alignItems="center" sx={{width: '100%'}}>
					<LinearProgress
						color={'primary'}
						variant={visibleSummary.total === 0 || done === 0 ? 'indeterminate' : 'determinate'}
						value={progress}
						sx={{
							width: '100%',
						}}
					/>
					<Typography variant="h5">{progressLabel}</Typography>
				</Stack>

				{visibleSummary.failed > 0 ? <Alert severity="warning" variant="outlined" sx={{ width: '100%', maxWidth: 760 }}>
					{t('{{count}} product could not be published.', { count: visibleSummary.failed })}
				</Alert> : null}

				<ProductPublishJobList store={props.store} batchId={props.batchId} />

				<Button
					variant="outlined"
					color="secondary"
					size="large"
					onClick={props.onDialogClose}
				>{t('Close')}</Button>
			</Stack>
		</DialogContent>
	</>
}
